import React, { useState } from 'react';
import { Button, Input, Typography, Table, Tag, Spin, Card, Collapse, Toast, Space, Modal, Tooltip } from '@douyinfe/semi-ui';
import { IconSearch, IconCopy, IconDownload } from '@douyinfe/semi-icons';
import { API, timestamp2string } from '../helpers';
import { stringToColor } from '../helpers/render';
import { ITEMS_PER_PAGE } from '../constants';
import { renderModelPrice, renderQuota } from '../helpers/render';
import Paragraph from '@douyinfe/semi-ui/lib/es/typography/paragraph';
import Papa from 'papaparse';

const { Text } = Typography;
const { Panel } = Collapse;
const TOKEN_LOG_PAGE_SIZE = 100;

async function fetchAllTokenLogs(baseUrl, apikey, onProgress) {
    const allLogs = [];
    let page = 1;
    let total = 0;

    while (true) {
        const logRes = await API.get(`${baseUrl}/api/log/token`, {
            headers: { Authorization: `Bearer ${apikey}` },
            params: {
                paged: 1,
                p: page,
                page_size: TOKEN_LOG_PAGE_SIZE,
            },
        });
        const { success, message, data: logData } = logRes?.data || {};
        if (!success) {
            throw new Error(message || '查询调用详情失败');
        }

        if (Array.isArray(logData)) {
            return [...logData].reverse();
        }

        const items = Array.isArray(logData?.items) ? logData.items : [];
        total = Number(logData?.total || total || items.length);
        allLogs.push(...items);
        if (typeof onProgress === 'function') {
            const loaded = total > 0 ? Math.min(allLogs.length, total) : allLogs.length;
            onProgress(total > 0 ? `正在加载调用记录 ${loaded} / ${total}` : `正在加载调用记录 ${loaded}`);
        }

        if (!logData?.has_more || items.length === 0) {
            break;
        }
        page += 1;
    }

    return allLogs.reverse();
}

function renderTimestamp(timestamp) {
    return timestamp2string(timestamp);
}

function renderIsStream(bool) {
    if (bool) {
        return <Tag color="blue" size="large">流</Tag>;
    }
    return <Tag color="purple" size="large">非流</Tag>;
}

function renderUseTime(type) {
    const time = parseInt(type, 10);
    if (time < 101) {
        return <Tag color="green" size="large">{time} 秒</Tag>;
    }
    if (time < 300) {
        return <Tag color="orange" size="large">{time} 秒</Tag>;
    }
    return <Tag color="red" size="large">{time} 秒</Tag>;
}

function renderExpiryDescription(activeTabData) {
    if (!activeTabData.tokenValid) {
        return '未知';
    }
    if (activeTabData.expireDurationDays > 0 && !activeTabData.firstUsedTime) {
        return `时限卡，尚未使用，暂不计算到期时间（首用后有效 ${activeTabData.expireDurationDays} 天）`;
    }
    if (activeTabData.expiresAt === 0) {
        return '永不过期';
    }
    return renderTimestamp(activeTabData.expiresAt);
}

function renderDailyQuotaDescription(activeTabData) {
    if (!activeTabData.tokenValid) {
        return '未知';
    }
    if (!activeTabData.dailyQuotaLimit || activeTabData.dailyQuotaLimit <= 0) {
        return '无限制';
    }
    const used = renderQuota(activeTabData.dailyQuotaUsed || 0, 3);
    const limit = renderQuota(activeTabData.dailyQuotaLimit, 3);
    return activeTabData.dailyQuotaBlocked ? `${used} / ${limit}（今日已用尽）` : `${used} / ${limit}`;
}

const emptyTokenState = {
    totalGranted: 0,
    totalUsed: 0,
    totalAvailable: 0,
    unlimitedQuota: false,
    expiresAt: 0,
    firstUsedTime: 0,
    expireDurationDays: 0,
    dailyQuotaLimit: 0,
    dailyQuotaUsed: 0,
    dailyQuotaRemaining: 0,
    dailyQuotaBlocked: false,
    dailyQuotaResetAt: 0,
    tokenName: '',
    logs: [],
    tokenValid: false,
};

const TokenQueryPanel = ({ baseUrl }) => {
    const [apikey, setAPIKey] = useState('');
    const [loading, setLoading] = useState(false);
    const [logLoadingMessage, setLogLoadingMessage] = useState('');
    const [activeKeys, setActiveKeys] = useState([]);
    const [pageSize, setPageSize] = useState(ITEMS_PER_PAGE);
    const [tabData, setTabData] = useState(emptyTokenState);

    const resetData = () => {
        setTabData(emptyTokenState);
    };

    const fetchData = async () => {
        if (apikey === '') {
            Toast.warning('请先输入令牌，再进行查询');
            return;
        }
        if (!/^sk-[a-zA-Z0-9]{48}$/.test(apikey)) {
            Toast.error('令牌格式非法！');
            return;
        }

        setLoading(true);
        setLogLoadingMessage('');
        let newTabData = {
            ...emptyTokenState,
        };

        try {
            if (process.env.REACT_APP_SHOW_BALANCE === 'true') {
                const usageRes = await API.get(`${baseUrl}/api/usage/token/`, {
                    headers: { Authorization: `Bearer ${apikey}` },
                });
                const usageData = usageRes?.data;
                if (usageData?.code) {
                    const d = usageData.data;
                    newTabData.unlimitedQuota = d.unlimited_quota;
                    newTabData.totalGranted = d.total_granted;
                    newTabData.totalUsed = d.total_used;
                    newTabData.totalAvailable = d.total_available;
                    newTabData.expiresAt = d.expires_at;
                    newTabData.firstUsedTime = d.first_used_time || 0;
                    newTabData.expireDurationDays = d.expire_duration_days || 0;
                    newTabData.dailyQuotaLimit = d.daily_quota_limit || 0;
                    newTabData.dailyQuotaUsed = d.daily_quota_used || 0;
                    newTabData.dailyQuotaRemaining = d.daily_quota_remaining || 0;
                    newTabData.dailyQuotaBlocked = d.daily_quota_blocked || false;
                    newTabData.dailyQuotaResetAt = d.daily_quota_reset_at || 0;
                    newTabData.tokenName = d.name;
                    newTabData.tokenValid = true;
                } else {
                    Toast.error(usageData?.message || '查询令牌信息失败');
                }
            }
        } catch (e) {
            console.log(e);
            Toast.error('查询令牌信息失败，请检查令牌是否正确');
            resetData();
            setLoading(false);
            return;
        }

        try {
            if (process.env.REACT_APP_SHOW_DETAIL === 'true') {
                const logs = await fetchAllTokenLogs(baseUrl, apikey, setLogLoadingMessage);
                newTabData.logs = logs;
                setActiveKeys(logs.length > 0 ? ['1', '2'] : ['1']);
            }
        } catch (e) {
            console.log(e);
            Toast.error(e?.message || '查询调用详情失败，请稍后重试');
        }

        setTabData(newTabData);
        setLogLoadingMessage('');
        setLoading(false);
    };

    const copyText = async (text) => {
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(text);
                Toast.success(`已复制：${text}`);
                return;
            }

            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            textArea.style.top = '-999999px';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();

            try {
                document.execCommand('copy');
                textArea.remove();
                Toast.success(`已复制：${text}`);
            } catch (err) {
                textArea.remove();
                Modal.error({ title: '无法复制到剪贴板，请手动复制', content: text });
            }
        } catch (err) {
            Modal.error({ title: '无法复制到剪贴板，请手动复制', content: text });
        }
    };

    const columns = [
        {
            title: '时间',
            dataIndex: 'created_at',
            render: renderTimestamp,
            sorter: (a, b) => a.created_at - b.created_at,
        },
        {
            title: '令牌名称',
            dataIndex: 'token_name',
            render: (text, record) => record.type === 0 || record.type === 2 ? (
                <Tag color="grey" size="large" onClick={() => copyText(text)}>{text}</Tag>
            ) : null,
            sorter: (a, b) => ('' + a.token_name).localeCompare(b.token_name),
        },
        {
            title: '模型',
            dataIndex: 'model_name',
            render: (text, record) => record.type === 0 || record.type === 2 ? (
                <Tag color={stringToColor(text)} size="large" onClick={() => copyText(text)}>{text}</Tag>
            ) : null,
            sorter: (a, b) => ('' + a.model_name).localeCompare(b.model_name),
        },
        {
            title: '用时',
            dataIndex: 'use_time',
            render: (text, record) => record.model_name.startsWith('mj_') ? null : (
                <Space>
                    {renderUseTime(text)}
                    {renderIsStream(record.is_stream)}
                </Space>
            ),
            sorter: (a, b) => a.use_time - b.use_time,
        },
        {
            title: '提示',
            dataIndex: 'prompt_tokens',
            render: (text, record) => record.model_name.startsWith('mj_') ? null : (
                record.type === 0 || record.type === 2 ? <span>{text}</span> : null
            ),
            sorter: (a, b) => a.prompt_tokens - b.prompt_tokens,
        },
        {
            title: '补全',
            dataIndex: 'completion_tokens',
            render: (text, record) => parseInt(text, 10) > 0 && (record.type === 0 || record.type === 2) ? (
                <span>{text}</span>
            ) : null,
            sorter: (a, b) => a.completion_tokens - b.completion_tokens,
        },
        {
            title: '花费',
            dataIndex: 'quota',
            render: (text, record) => record.type === 0 || record.type === 2 ? <div>{renderQuota(text, 6)}</div> : null,
            sorter: (a, b) => a.quota - b.quota,
        },
        {
            title: '详情',
            dataIndex: 'content',
            render: (text, record) => {
                let other = null;
                try {
                    const rawOther = record.other === '' ? '{}' : record.other;
                    other = JSON.parse(rawOther);
                } catch (e) {
                    return (
                        <Tooltip content="该版本不支持显示计算详情">
                            <Paragraph ellipsis={{ rows: 2 }}>{text}</Paragraph>
                        </Tooltip>
                    );
                }
                if (other == null) {
                    return (
                        <Paragraph ellipsis={{ rows: 2, showTooltip: { type: 'popover' } }}>
                            {text}
                        </Paragraph>
                    );
                }
                const content = renderModelPrice(
                    record.prompt_tokens,
                    record.completion_tokens,
                    other.model_ratio,
                    other.model_price,
                    other.completion_ratio,
                    other.group_ratio,
                );
                return (
                    <Tooltip content={content}>
                        <Paragraph ellipsis={{ rows: 2 }}>{text}</Paragraph>
                    </Tooltip>
                );
            },
        },
    ];

    const copyTokenInfo = (e) => {
        e.stopPropagation();
        const info = `令牌名称: ${tabData.tokenName || '未知'}
令牌总额: ${tabData.unlimitedQuota ? '无限' : !tabData.tokenValid ? '未知' : renderQuota(tabData.totalGranted, 3)}
剩余额度: ${tabData.unlimitedQuota ? '无限制' : !tabData.tokenValid ? '未知' : renderQuota(tabData.totalAvailable, 3)}
已用额度: ${tabData.unlimitedQuota ? '不进行计算' : !tabData.tokenValid ? '未知' : renderQuota(tabData.totalUsed, 3)}
有效期至: ${renderExpiryDescription(tabData)}
今日配额使用: ${renderDailyQuotaDescription(tabData)}`;
        copyText(info);
    };

    const exportCSV = (e) => {
        e.stopPropagation();
        const csvData = tabData.logs.map((log) => ({
            时间: renderTimestamp(log.created_at),
            模型: log.model_name,
            用时: log.use_time,
            提示: log.prompt_tokens,
            补全: log.completion_tokens,
            花费: log.quota,
            详情: log.content,
        }));
        const csvString = '\ufeff' + Papa.unparse(csvData);

        try {
            const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'data.csv';
            if (navigator.userAgent.indexOf('Safari') > -1 && navigator.userAgent.indexOf('Chrome') === -1) {
                link.target = '_blank';
                link.setAttribute('target', '_blank');
            }
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => URL.revokeObjectURL(url), 100);
        } catch (err) {
            Toast.error('导出失败，请稍后重试');
            console.error('Export failed:', err);
        }
    };

    return (
        <>
            <Card style={{ marginTop: 24 }}>
                <Input
                    showClear
                    value={apikey}
                    onChange={(value) => setAPIKey(value)}
                    placeholder="请输入要查询的令牌 sk-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    prefix={<IconSearch />}
                    suffix={(
                        <Button
                            type="primary"
                            theme="solid"
                            onClick={fetchData}
                            loading={loading}
                            disabled={apikey === ''}
                        >
                            查询
                        </Button>
                    )}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            fetchData();
                        }
                    }}
                />
            </Card>
            <Card style={{ marginTop: 24 }}>
                <Collapse activeKey={activeKeys} onChange={(keys) => setActiveKeys(keys)}>
                    {process.env.REACT_APP_SHOW_BALANCE === 'true' && (
                        <Panel
                            header="令牌信息"
                            itemKey="1"
                            extra={(
                                <Button
                                    icon={<IconCopy />}
                                    theme="borderless"
                                    type="primary"
                                    onClick={copyTokenInfo}
                                    disabled={!tabData.tokenValid}
                                >
                                    复制令牌信息
                                </Button>
                            )}
                        >
                            <Spin spinning={loading} tip={logLoadingMessage || undefined}>
                                <div style={{ marginBottom: 16 }}>
                                    <Text type="secondary">令牌名称：{tabData.tokenName || '未知'}</Text>
                                    <br /><br />
                                    <Text type="secondary">令牌总额：{tabData.unlimitedQuota ? '无限' : !tabData.tokenValid ? '未知' : renderQuota(tabData.totalGranted, 3)}</Text>
                                    <br /><br />
                                    <Text type="secondary">剩余额度：{tabData.unlimitedQuota ? '无限制' : !tabData.tokenValid ? '未知' : renderQuota(tabData.totalAvailable, 3)}</Text>
                                    <br /><br />
                                    <Text type="secondary">已用额度：{tabData.unlimitedQuota ? '不进行计算' : !tabData.tokenValid ? '未知' : renderQuota(tabData.totalUsed, 3)}</Text>
                                    <br /><br />
                                    <Text type="secondary">有效期至：{renderExpiryDescription(tabData)}</Text>
                                    <br /><br />
                                    <Text type="secondary">今日配额使用：{renderDailyQuotaDescription(tabData)}</Text>
                                </div>
                            </Spin>
                        </Panel>
                    )}
                    {process.env.REACT_APP_SHOW_DETAIL === 'true' && (
                        <Panel
                            header="调用详情"
                            itemKey="2"
                            extra={(
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <Tag shape="circle" color="green" style={{ marginRight: 5 }}>
                                        计算汇率：$1 = 50 0000 tokens
                                    </Tag>
                                    <Button
                                        icon={<IconDownload />}
                                        theme="borderless"
                                        type="primary"
                                        onClick={exportCSV}
                                        disabled={!tabData.tokenValid || tabData.logs.length === 0}
                                    >
                                        导出为CSV文件
                                    </Button>
                                </div>
                            )}
                        >
                            <Spin spinning={loading} tip={logLoadingMessage || undefined}>
                                <Table
                                    columns={columns}
                                    dataSource={tabData.logs}
                                    pagination={{
                                        pageSize,
                                        hideOnSinglePage: true,
                                        showSizeChanger: true,
                                        pageSizeOpts: [10, 20, 50, 100],
                                        onPageSizeChange: (size) => setPageSize(size),
                                        showTotal: (total) => `共 ${total} 条`,
                                        showQuickJumper: true,
                                        total: tabData.logs.length,
                                        style: { marginTop: 12 },
                                    }}
                                />
                            </Spin>
                        </Panel>
                    )}
                </Collapse>
            </Card>
        </>
    );
};

export default TokenQueryPanel;
